// Turns the admin-authored rich-text (h2/h3/p/ul) into a structured tree
// so the guide page can render dedicated layouts (pricing cards, checklist,
// info grid) instead of dumping raw HTML.

export type GuideGroup = {
  title: string;
  eyebrow: string | null;
  total: string | null;
  introHtml: string;
  items: string[]; // raw <li> innerHTML
};

export type GuideSection = {
  id: string;
  title: string;
  introHtml: string;
  groups: GuideGroup[];
};

export type GuideKind = "pricing" | "checklist" | "grid";

const slugify = (text: string) =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "section";

/** "Médecine humaine — De 50% à 69% (Total : 70$)" -> { eyebrow, title, total } */
const parseTierTitle = (raw: string): { title: string; eyebrow: string | null; total: string | null } => {
  let text = raw.trim();
  let total: string | null = null;
  const totalMatch = text.match(/\(Total\s*:?\s*([^)]+)\)/i);
  if (totalMatch) {
    total = totalMatch[1].trim();
    text = text.replace(totalMatch[0], "").trim();
  }
  let eyebrow: string | null = null;
  const emDashIdx = text.indexOf("—");
  if (emDashIdx > -1) {
    eyebrow = text.slice(0, emDashIdx).trim();
    text = text.slice(emDashIdx + 1).trim();
  }
  return { title: text, eyebrow, total };
};

export function parseGuideHtml(html: string): GuideSection[] {
  if (!html || typeof window === "undefined") return [];

  const doc = new DOMParser().parseFromString(html, "text/html");
  const nodes = Array.from(doc.body.children);

  const sections: GuideSection[] = [];
  let currentSection: GuideSection | null = null;
  let currentGroup: GuideGroup | null = null;

  const closeGroup = () => {
    if (currentSection && currentGroup) currentSection.groups.push(currentGroup);
    currentGroup = null;
  };

  for (const node of nodes) {
    const tag = node.tagName.toLowerCase();

    if (tag === "h1" || tag === "h2") {
      closeGroup();
      const title = node.textContent?.trim() || "";
      currentSection = { id: slugify(title), title, introHtml: "", groups: [] };
      sections.push(currentSection);
    } else if (tag === "h3" || tag === "h4") {
      closeGroup();
      if (!currentSection) continue;
      const parsed = parseTierTitle(node.textContent || "");
      currentGroup = { title: parsed.title, eyebrow: parsed.eyebrow, total: parsed.total, introHtml: "", items: [] };
    } else if (tag === "ul" || tag === "ol") {
      const items = Array.from(node.children).map((li) => li.innerHTML);
      if (currentGroup) {
        currentGroup.items.push(...items);
      } else if (currentSection) {
        currentSection.groups.push({ title: "", eyebrow: null, total: null, introHtml: "", items });
      }
    } else if (tag === "p" || tag === "blockquote") {
      if (currentGroup) currentGroup.introHtml += node.outerHTML;
      else if (currentSection) currentSection.introHtml += node.outerHTML;
    }
  }
  closeGroup();

  return sections;
}

export function detectSectionKind(section: GuideSection): GuideKind {
  const namedGroups = section.groups.filter((g) => g.title.trim().length > 0);
  if (namedGroups.length === 0) return "checklist";

  const looksPricing =
    /frais|tarif|prix|paiement/i.test(section.title) ||
    namedGroups.some((g) => g.total || /%/.test(g.title) || /\$/.test(g.title));
  if (looksPricing) return "pricing";

  return "grid";
}

/** "Frais d'inscription : 20$" -> { label: "Frais d'inscription", amount: "20$" } */
export function splitAmountLine(itemHtml: string): { label: string; amount: string } | null {
  const text = itemHtml.replace(/<[^>]+>/g, "").trim();
  const m = text.match(/^(.+?)\s*:\s*([\d][\d.,]*\s*\$)\s*$/);
  if (!m) return null;
  return { label: m[1].trim(), amount: m[2].trim() };
}
